/**
 * @fullvsl/app-shell/dom — minimal DOM creation helpers for the app shell.
 *
 * Framework-light helper: `el()` creates an element with optional class, text,
 * attributes, and children. `svg()` creates inline SVG icons. Matches the
 * utilitarian, no-dependency convention used across the FullVSL codebase.
 */

export type ElAttrs = Record<string, string | boolean | undefined | null>;

/** Create an element with tag, className, text content, attributes, and children. */
export function el(
  tag: string,
  className?: string | null,
  text?: string | null,
  attrs?: ElAttrs,
): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      node.setAttribute(k, v === true ? "" : String(v));
    }
  }
  return node;
}

/** Append multiple children to a parent. */
export function append(parent: HTMLElement, ...children: (Node | null | undefined)[]): void {
  for (const c of children) {
    if (c) parent.appendChild(c);
  }
}

/** Create a text node. */
export function txt(s: string): Text {
  return document.createTextNode(s);
}

/** Create an inline SVG icon element from a path string. */
export function icon(pathD: string, className = "fva-nav-icon"): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "currentColor");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add(...className.split(/\s+/).filter(Boolean));
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", pathD);
  svg.appendChild(path);
  return svg;
}

/** SVG icon path data for nav items and UI controls. */
export const ICONS = {
  list: "M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z",
  plus: "M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z",
  edit: "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75z",
  play: "M8 5v14l11-7z",
  chart: "M5 9.2h3V19H5zm5.6-4.2h2.8v14h-2.8zm5.6 8H19V19h-2.8z",
  back: "M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z",
  copy: "M16 1H4a2 2 0 0 0-2 2v14h2V3h12zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11z",
  trash: "M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z",
  // Design-system nav icons (Material Symbols-style outlines)
  videoLibrary: "M4 6H2v14a2 2 0 0 0 2 2h14v-2H4zm16-4H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm0 14H8V4h12zm-7-1 5.5-4L13 7z",
  analytics: "M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM9 17H7v-7h2zm4 0h-2V7h2zm4 0h-2v-4h2z",
  templates: "M3 3v18h18V3zm2 2h14v5H5zm0 7h6v7H5zm8 0h6v7h-6z",
  settings: "M19.14 12.94a7.49 7.49 0 0 0 .05-.94 7.49 7.49 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.61-.22l-2.39.96a7.03 7.03 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54c-.59.24-1.13.56-1.62.94l-2.39-.96a.5.5 0 0 0-.61.22L2.74 8.84a.5.5 0 0 0 .12.64l2.03 1.58a7.49 7.49 0 0 0 0 1.88l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.42.34.68.22l2.36-.96c.49.38 1.03.7 1.62.94l.36 2.54c.04.24.25.42.5.42h3.84c.25 0 .46-.18.5-.42l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.36.96c.26.12.54.02.68-.22l1.92-3.32a.5.5 0 0 0-.12-.64zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z",
  extension: "M20.5 11H19V7a2 2 0 0 0-2-2h-4V3.5a2.5 2.5 0 0 0-5 0V5H4a2 2 0 0 0-2 2v3.8h1.5a2.5 2.5 0 0 1 0 5H2V19a2 2 0 0 0 2 2h3.8v-1.5a2.5 2.5 0 0 1 5 0V21H17a2 2 0 0 0 2-2v-4h1.5a2.5 2.5 0 0 0 0-5z",
  search: "M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z",
  notifications: "M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6v-5a6 6 0 0 0-5-5.91V4a1 1 0 0 0-2 0v1.09A6 6 0 0 0 6 11v5l-2 2v1h16v-1z",
  help: "M11 18h2v-2h-2zm1-16A10 10 0 1 0 22 12 10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm0-14a4 4 0 0 0-4 4h2a2 2 0 1 1 2 2c-.55 0-1 .45-1 1v2h2v-1a2 2 0 0 0 1-1 4 4 0 0 0-2-7z",
  logout: "M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8v-2H4z",
  filter: "M10 18h4v-2h-4zM3 6v2h18V6zm3 7h12v-2H6z",
  trendingUp: "M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z",
  trendingDown: "M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z",
  moreVert: "M12 8a2 2 0 1 0-2-2 2 2 0 0 0 2 2zm0 2a2 2 0 1 0 2 2 2 2 0 0 1-2-2zm0 6a2 2 0 1 0 2 2 2 2 0 0 1-2-2z",
  rocket: "M9.19 6.35c-2.04 2.29-3.44 5.58-3.57 5.89L2 10.69l3.11-3.11c.39-.39.93-.58 1.47-.42l2.61.69-1 2.5zm8.09 5.75c-2.77-1.16-5.44-3.31-7.21-6.03.31-1.34 1.34-3.07 3.57-4.3 3.2-1.76 6.16-1.85 6.88-1.85.51 0 .96.13 1.3.42.29.34.42.79.42 1.3 0 .72-.09 3.68-1.85 6.88-1.23 2.23-2.96 3.26-4.3 3.57zM15 6.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-5.81 11.15C7.15 15.62 5 12.95 3.84 10.18c.31-1.34 1.34-3.07 3.57-4.3 3.2-1.76 6.16-1.85 6.88-1.85-1.39 2.04-1.96 4.42-1.96 6.85 0 1.61.4 3.17 1.13 4.55-.71-.02-1.34-.21-1.84-.58-.39-.39-.59-.93-.43-1.2zM6 16c-1.1 0-2 .9-2 2 0 .84-.5 1.55-1 2 .5.45 1.16 1 2 1 1.66 0 3-1.34 3-3 0-1.1-.9-2-2-2z",
  code: "M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6z",
  calendar: "M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V10h14zm0-12H5V6h14z",
  download: "M19 9h-4V3H9v6H5l7 7zm-5 4v6H10v-6H5l7-7 7 7z",
  checkCircle: "M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z",
  visibilityOff: "M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 0 0 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65a3 3 0 0 0 3 3c.22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53a5 5 0 0 1-5-5c0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16a3 3 0 0 0-3-3z",
  smartphone: "M17 1.01 7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10z",
  desktop: "M21 2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7l-2 3v1h8v-1l-2-3h7c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H3V4h18z",
  newReleases: "M23 12l-2.44-2.78.34-3.68-3.61-.82-1.89-3.18L12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68zm-4.51 1.93.52 2-1.9 1.1-1.94.94-.52-2L12 16.5l-2.71 1.81-.52 2-1.94-.94-1.9-1.1.52-2-1.74-2.43 1.74-2.43-.52-2 1.9-1.1 1.94-.94.52 2L12 7.5l2.71-1.81.52-2 1.94.94 1.9 1.1-.52 2L19.23 12zM13 14h-2v2h2zm0-8h-2v6h2z",
  insight: "M11 18h2v-2h-2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z",
} as const;

/** Create a labeled form field wrapper. */
export function field(
  labelText: string,
  input: HTMLElement,
  hint?: string,
): HTMLElement {
  const wrap = el("div", "fva-field");
  const label = el("label", "fva-label", labelText);
  if (hint) {
    const h = el("span", "fva-label-hint", hint);
    label.appendChild(h);
  }
  append(wrap, label, input);
  return wrap;
}

/** Format a date for display. */
export function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
